import { morph } from "@theme/morph";
import { Component } from "@theme/component";
import {
  CartUpdateEvent,
  ThemeEvents,
  VariantSelectedEvent,
} from "@theme/events";
import { DialogComponent, DialogCloseEvent } from "@theme/dialog";
import {
  mediaQueryLarge,
  isMobileBreakpoint,
  getIOSVersion,
} from "@theme/utilities";

/** Id of the latest quick add click: only that one may fill the shared drawer and clear its loading state. */
let latestQuickAddRequest = 0;

/**
 * Checks the first available size in the rendered (already sorted) option order.
 * Shopify's own default, the card's variant and ?variant= all follow the unsorted variant order,
 * so the size option is always re-picked from the sorted list.
 * @param {Element} modalContent - The modal content element
 */
function selectFirstAvailableVariant(modalContent) {
  const inputs = /** @type {HTMLInputElement[]} */ ([
    ...modalContent.querySelectorAll(
      "fieldset[data-size-option] input[type=\"radio\"][data-variant-id]",
    ),
  ]);
  // All sizes sold out: fall back to the first size in the sorted list
  const target =
    inputs.find((input) => input.dataset.optionAvailable !== "false") ??
    inputs[0];
  if (!target || target.checked) return;

  target.checked = true;
  target.dispatchEvent(new Event("change", { bubbles: true }));
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  get productPageUrl() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest("product-card")
      );
    const hotspotProduct =
      /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
        this.closest("product-hotspot-component")
      );
    const productLink =
      productCard?.getProductCardLink() ||
      hotspotProduct?.getHotspotProductLink() ||
      this.closest("product-card")?.querySelector(
        "[data-bloomreach-product-link]",
      );

    if (!productLink?.href) return "";

    const url = new URL(productLink.href);
    url.searchParams.set("view", "quick-add");

    // No variant is forwarded from the card: the first available sorted size is picked after render
    url.searchParams.delete("variant");

    return url.toString();
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener("change", this.#closeQuickAddModal);
    document.addEventListener(
      ThemeEvents.variantSelected,
      this.#updateQuickAddButtonState.bind(this),
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener("change", this.#closeQuickAddModal);
    this.#abortController?.abort();
    document.removeEventListener(
      ThemeEvents.variantSelected,
      this.#updateQuickAddButtonState.bind(this),
    );
  }

  /**
   * Handles quick add button click.
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    // Open the drawer straight away and load the product inside it, so the click gets instant feedback
    const request = ++latestQuickAddRequest;
    dialogComponent.toggleAttribute("loading", true);
    this.#openQuickAddModal();

    try {
      const html = await this.fetchProductPage(this.productPageUrl);
      // A newer quick add (another card) took over the drawer, or it was closed meanwhile.
      // Read the drawer's own <dialog>: refs.dialog gets overwritten by nested dialogs in the morphed
      // product content (notify-me-dialog, zoom-dialog), which stay closed and would block every later update.
      const drawer = dialogComponent.querySelector(":scope > dialog");
      if (request !== latestQuickAddRequest || !(drawer instanceof HTMLDialogElement && drawer.open)) return;

      const productGrid = html?.querySelector("[data-product-grid-content]");
      // Nothing to show: closing beats revealing the previous product's content
      if (!productGrid) {
        dialogComponent.closeDialog();
        return;
      }
      await this.updateQuickAddModal(
        /** @type {Element} */ (productGrid.cloneNode(true)),
      );

      this.#resetScroll();
      this.#trackQuickView(dialogComponent);
    } catch (error) {
      console.error("[QuickAdd] handleClick failed", error);
      if (request === latestQuickAddRequest) dialogComponent.closeDialog();
    } finally {
      if (request === latestQuickAddRequest) dialogComponent.toggleAttribute("loading", false);
    }
  };

  /**
   * Sends the Bloomreach view_quickview event once the quick-add dialog is open.
   * The theme owns it for every quick-add (the web pixel's quick_view_tracking toggle must be off,
   * otherwise the embed's own bre.quick_view adds a second event). Calls made before consent are
   * queued by the SDK until the app embed starts it.
   * @param {HTMLElement} dialogComponent
   */
  #trackQuickView(dialogComponent) {
    const sdk = window.exponea;
    if (!sdk || typeof sdk.track !== "function") return;
    const card = this.closest("[data-br-pid], [data-br-product-id]");
    const productId =
      card?.getAttribute("data-br-pid") ||
      card?.getAttribute("data-br-product-id") ||
      this.querySelector("product-form-component")?.getAttribute("data-product-id") ||
      dialogComponent.querySelector("product-form-component")?.getAttribute("data-product-id");
    if (!productId) return;
    const variantInput = /** @type {HTMLInputElement | null} */ (
      dialogComponent.querySelector('form input[name="id"]') ||
        card?.querySelector('input[name="id"]') ||
        null
    );
    sdk.track("view_quickview", {
      product_id: productId,
      title: this.dataset.productTitle || "",
      variant_id: variantInput?.value || "",
    });
  }

  /**
   * Fetches and replaces the complete quick-add product content.
   * @param {string} productUrl
   * @param {string | null} [variantId]
   * @returns {Promise<void>}
   */
  async replaceProduct(productUrl, variantId = null) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set("view", "quick-add");
    if (variantId) url.searchParams.set("variant", variantId);

    const html = await this.fetchProductPage(url.toString());
    const productGrid = html?.querySelector("[data-product-grid-content]");
    if (!productGrid) return;

    const productTitle = productGrid.querySelector("h1")?.textContent?.trim();
    if (productTitle) this.dataset.productTitle = productTitle;

    await this.updateQuickAddModal(
      /** @type {Element} */ (productGrid.cloneNode(true)),
    );
  }

  #resetScroll() {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector(".product-details");
    const productMedia = dialogComponent.querySelector(
      ".product-information__media",
    );
    productDetails?.scrollTo({ top: 0, behavior: "instant" });
    productMedia?.scrollTo({ top: 0, behavior: "instant" });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute("stay-visible", true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute("stay-visible", false);
      },
      {
        once: true,
      },
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    const dialog = dialogComponent?.querySelector("dialog");
    const dialogElement = dialog instanceof HTMLDialogElement ? dialog : null;
    console.log("[QuickAdd] open modal lookup", {
      component: dialogComponent,
      isQuickAddDialog: dialogComponent instanceof QuickAddDialog,
      dialog,
      dialogOpenBefore: dialogElement?.open,
      bodyPositionBefore: document.body.style.position,
    });
    if (!(dialogComponent instanceof QuickAddDialog)) {
      console.error("[QuickAdd] quick-add-dialog instance check failed", {
        constructor: dialogComponent?.constructor?.name,
        customElementDefined: customElements.get("quick-add-dialog")?.name,
      });
      return;
    }

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    try {
      dialogComponent.showDialog();
      dialogComponent.dispatchEvent(
        new Event("hm:pdp-countdown:refresh", { bubbles: true }),
      );
      console.log("[QuickAdd] showDialog returned", {
        dialogOpenAfter: dialogElement?.open,
        hasOpenAttribute: dialogElement?.hasAttribute("open"),
        bodyPositionAfter: document.body.style.position,
        bodyTopAfter: document.body.style.top,
      });
    } catch (error) {
      console.error("[QuickAdd] showDialog threw", error);
      return;
    }

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialogRef = dialogComponent.querySelector(":scope > dialog");
    const dialogRefElement =
      dialogRef instanceof HTMLDialogElement ? dialogRef : null;
    console.log("[QuickAdd] direct dialog after showDialog", {
      dialogRef,
      dialogOpen: dialogRefElement?.open,
      dialogClass: dialogRefElement?.className,
    });
    if (!(dialogRef instanceof HTMLDialogElement)) {
      console.error("[QuickAdd] direct dialog is missing");
      return;
    }
    dialogRef.addEventListener("animationstart", this.#resetScroll.bind(this), {
      once: true,
    });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      console.log("[QuickAdd] fetch start", productPageUrl);
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });
      console.log("[QuickAdd] fetch response", {
        url: response.url,
        status: response.status,
        ok: response.ok,
        contentType: response.headers.get("content-type"),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`,
        );
      }

      const responseText = await response.text();
      console.log("[QuickAdd] fetch body", {
        length: responseText.length,
        hasQuickAddGrid: responseText.includes("data-product-grid-content"),
      });
      const html = new DOMParser().parseFromString(responseText, "text/html");

      return html;
    } catch (error) {
      if (error.name === "AbortError") {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById("quick-add-modal-content");
    console.log("[QuickAdd] update modal lookup", {
      productGrid,
      modalContent,
      modalContentChildren: modalContent?.childElementCount,
    });

    if (!productGrid || !modalContent) {
      console.error("[QuickAdd] update modal skipped", {
        hasProductGrid: Boolean(productGrid),
        hasModalContent: Boolean(modalContent),
      });
      return;
    }

    console.log("[QuickAdd] before morph", {
      modalContentChildren: modalContent.childElementCount,
      productGridChildren: productGrid.childElementCount,
    });
    resetSizeGuide(modalContent);
    morph(modalContent, productGrid);
    modalContent.dispatchEvent(
      new Event("hm:pdp-countdown:refresh", { bubbles: true }),
    );
    console.log("[QuickAdd] after morph", {
      modalContentChildren: modalContent.childElementCount,
      modalContentHtml: modalContent.innerHTML.slice(0, 300),
    });

    selectFirstAvailableVariant(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest("product-card") !== this.closest("product-card"))
      return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === "1" ? "add" : "choose";
    this.setAttribute("data-quick-add-button", quickAddButton);
  }
}

if (!customElements.get("quick-add-component")) {
  customElements.define("quick-add-component", QuickAddComponent);
}

/**
 * Removes the previous product's size guide before a morph. hm-size-guide.js rewrites that DOM
 * (accordion wrap, stripped styles, tab indicator), so morphing the next product into it reuses the
 * mutated nodes and skips connectedCallback. Dropping it lets the new markup mount and initialise fresh.
 * @param {Element} modalContent
 */
function resetSizeGuide(modalContent) {
  modalContent.querySelectorAll(".hm-size-guide-trigger-wrap").forEach((el) => el.remove());
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#updateProductTitleLink,
    );
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#replaceChangedProduct,
      { capture: true },
    );

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener("click", this.#handleBackdropClick, {
      signal: this.#abortController.signal,
    });
  }

  /**
   * Closes the drawer on a backdrop click. Light dismiss is disabled on this element because the
   * content is morphed and holds nested dialogs, so the generic target-contains check misfires.
   * A backdrop click targets the <dialog> itself, never one of its descendants.
   * @param {MouseEvent} event
   */
  #handleBackdropClick = (event) => {
    const dialog = this.querySelector("dialog.quick-add-modal");
    if (!dialog || event.target !== dialog || !dialog.open) return;

    const { left, right, top, bottom } = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= left &&
      event.clientX <= right &&
      event.clientY >= top &&
      event.clientY <= bottom;
    if (!inside) this.closeDialog();
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose,
    );
    this.removeEventListener(
      ThemeEvents.variantUpdate,
      this.#replaceChangedProduct,
      { capture: true },
    );
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #replaceChangedProduct = async (/** @type {CustomEvent } */ event) => {
    const newProduct = event.detail?.data?.newProduct;
    if (!newProduct?.url) return;

    const url = new URL(newProduct.url, window.location.origin);
    url.searchParams.set("view", "quick-add");

    // No ?variant= here: the first available variant (sorted option order) is picked after the morph
    try {
      const response = await fetch(url);
      if (!response.ok)
        throw new Error(
          `Failed to fetch quick-add product: ${response.status}`,
        );

      const html = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
      );
      const newProductGrid = html.querySelector("[data-product-grid-content]");
      const modalContent = this.querySelector("#quick-add-modal-content");

      if (!newProductGrid || !modalContent) return;

      resetSizeGuide(modalContent);
      morph(modalContent, newProductGrid.cloneNode(true));
      modalContent.dispatchEvent(
        new Event("hm:pdp-countdown:refresh", { bubbles: true }),
      );
      selectFirstAvailableVariant(modalContent);
    } catch (error) {
      if (error.name !== "AbortError")
        console.error("[QuickAdd] product replacement failed", error);
    }
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector(".view-product-title a")
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector(".view-product-title a")
    );
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (
      this.querySelector(".product-header a")
    );

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (iosVersion.major === 16 && iosVersion.minor >= 4)
    )
      return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector("#ResultsList [product-grid-view]");
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = "";
        });
      }
    });
  };
}

if (!customElements.get("quick-add-dialog")) {
  customElements.define("quick-add-dialog", QuickAddDialog);
}
